import { useQuery, useQueryClient } from '@tanstack/react-query';

import { showSuccess } from '@components/toaster';
import { useThrottledMutation as useMutation } from '@hooks/use-throttled-mutation';
import { useScopedTranslate } from '@locale/translate';

import { useInvalidateJourneyActions } from 'api/journey-actions/hooks';
import { useInvalidateJourneys } from 'api/journeys/hooks';
import { handleError } from 'api/query-client';
import { isInTemplateRoute } from 'ui/router';

import {
  JourneyActionForm,
  createJourneyActionFormFile,
  createJourneyActionFormImage,
  getJourneyActionFormByJourneyActionId,
  getTemplateJourneyActionFormByJourneyActionId,
  loadForm,
  loadJourneyActionForm,
  loadJourneyActionForms,
  loadTemplateJourneyActionForm,
  loadTemplateJourneyActionForms,
  updateJourneyActionForm,
} from './requests';

const getJourneyActionFormKey = (formId: string) => ['journey-action-form', formId];

export const useInvalidateJourneyActionForms = () => {
  const queryClient = useQueryClient();

  const invalidateJourneyActionForms = (formId?: string) =>
    queryClient.invalidateQueries(getJourneyActionFormKey(formId ?? ''));

  const invalidateJourneyActionForm = (formId?: string) =>
    queryClient.invalidateQueries(['journey-action-form', formId]);

  const invalidateJourneyActionFormByJourneyActionId = () =>
    queryClient.invalidateQueries(['journey-action-form-by-journey-action-id']);

  const invalidateJourneyActionFormsList = () =>
    queryClient.invalidateQueries(['journey-action-forms']);

  return {
    invalidateJourneyActionForms,
    invalidateJourneyActionFormByJourneyActionId,
    invalidateJourneyActionForm,
    invalidateJourneyActionFormsList,
  };
};

export const useJourneyActionForm = (formId: string) => {
  const loadFn = isInTemplateRoute() ? loadTemplateJourneyActionForm : loadJourneyActionForm;
  return useQuery({
    queryKey: getJourneyActionFormKey(formId),
    queryFn: () => loadFn(formId),
    enabled: Boolean(formId),
  });
};

export const useJourneyActionForms = (options?: {
  keepPreviousData?: boolean;
  includePublished?: boolean;
  journeyId?: string;
  travellerJourneyId?: string;
}) => {
  const includePublished = options?.includePublished ?? false;

  const loadFn = isInTemplateRoute() ? loadTemplateJourneyActionForms : loadJourneyActionForms;
  return useQuery({
    queryKey: [
      'journey-action-forms',
      {
        includePublished,
        journeyId: options?.journeyId,
        travellerJourneyId: options?.travellerJourneyId,
      },
    ],
    queryFn: () =>
      loadFn({
        includePublished,
        journeyId: options?.journeyId,
        travellerJourneyId: options?.travellerJourneyId,
      }),
    keepPreviousData: options?.keepPreviousData,
  });
};

export const useUpdateJourneyActionForm = (
  formId: string,
  journeyActionId?: string,
  journeyId?: string
) => {
  const { t } = useScopedTranslate(locale);
  const {
    invalidateJourneyActionForm,
    invalidateJourneyActionForms,
    invalidateJourneyActionFormByJourneyActionId,
    invalidateJourneyActionFormsList,
  } = useInvalidateJourneyActionForms();
  const { invalidateJourneyAction } = useInvalidateJourneyActions();
  const { invalidateJourney } = useInvalidateJourneys();
  return useMutation({
    mutationFn: (data: Partial<JourneyActionForm>) => updateJourneyActionForm(formId, data),
    onSuccess: async () => {
      await invalidateJourneyActionForm(formId);
      invalidateJourneyAction(journeyActionId ?? '');
      invalidateJourney(journeyId ?? '');
      invalidateJourneyActionForms();
      invalidateJourneyActionFormByJourneyActionId();
      invalidateJourneyActionFormsList();
      showSuccess(t('formUpdated'));
    },
    onError: () => {
      handleError(t('formUpdateFailed'));
    },
  });
};

export const useCreateJourneyActionFormImage = () => {
  const { t } = useScopedTranslate(locale);

  return useMutation({
    mutationFn: createJourneyActionFormImage,
    onError: () => {
      handleError(t('imageSaveFailed'));
    },
  });
};

export const useCreateJourneyActionFormFile = () => {
  const { t } = useScopedTranslate(locale);

  return useMutation({
    mutationFn: createJourneyActionFormFile,
    onError: () => {
      handleError(t('fileSaveFailed'));
    },
  });
};

export const useJourneyActionFormByJourneyActionId = (
  journeyActionId: string,
  travellerJourneyId?: string
) => {
  const getFn = isInTemplateRoute()
    ? getTemplateJourneyActionFormByJourneyActionId
    : getJourneyActionFormByJourneyActionId;
  return useQuery({
    queryKey: ['journey-action-form-by-journey-action-id', journeyActionId, travellerJourneyId],
    queryFn: () => getFn(journeyActionId, travellerJourneyId),
    enabled: Boolean(journeyActionId),
  });
};

export const useForm = (shortId: string) => {
  return useQuery({
    queryKey: ['form', shortId],
    queryFn: () => loadForm(shortId),
  });
};

const locale = {
  en: {
    formUpdated: 'Form updated',
    formUpdateFailed: 'Failed to update form',
    imageSaveFailed: 'Failed to save image',
    fileSaveFailed: 'Failed to save file',
  },
};
